'use client';

import { useSearchParams } from 'next/navigation';
import { useCallback, useState } from 'react';

export const useModSearchParams = () => {
  const searchParams = useSearchParams();
  const [, forceUpdate] = useState({});

  function getNowSearchParams() {
    return new URL(location.toString()).searchParams;
  }

  function getCurSearchParams() {
    const curSearchParams = {} as Record<string, string>;
    getNowSearchParams()
      .entries()
      .forEach(([key, value]) => {
        curSearchParams[key] = value;
      });
    return curSearchParams;
  }

  function getQueryParams(searchParams: Record<string, string | number>) {
    let queryParams = '';
    let isFirst = true;
    Object.entries(searchParams).forEach(([key, value]) => {
      if (!value) {
        return;
      }
      queryParams += (isFirst ? '?' : '&') + `${key}=${value}`;
      isFirst = false;
    });

    return queryParams;
  }

  function getNowQueryParams() {
    return getQueryParams(getCurSearchParams());
  }

  function updateUrlQuery(changes: Record<string, string | number>) {
    const curSearchParams = getCurSearchParams();
    const newSearchParams = { ...curSearchParams, ...changes };
    const queryParams = getQueryParams(newSearchParams);

    // console.log('queryParams', queryParams);
    // https://github.com/vercel/next.js/discussions/47583#discussioncomment-7641185
    window.history.replaceState(window.history.state, '', queryParams);
  }

  const removeSearchParam = useCallback((key: string) => {
    const params = getNowSearchParams();

    if (!params.has(key)) return; // уже удалён → ничего не делаем

    params.delete(key);

    const newQuery = params.toString();
    const newSearch = newQuery ? `?${newQuery}` : '';

    // Самое важное: используем pathname + search
    const newUrl = window.location.pathname + newSearch;

    window.history.replaceState(null, '', newUrl);

    // Принудительно говорим React перечитать searchParams (помогает в ~80% случаев)
    forceUpdate({});
  }, []);

  const setSearchParam = useCallback(
    (key: string, value: string | number) => {
      // console.log('setSearchParam', `${key}=${value}`);
      updateUrlQuery({
        [key]: value,
      });
    },
    [searchParams]
  );

  const setSearchParams = useCallback(
    (changes: Record<string, string | number>, updateQuery = true) => {
      updateUrlQuery(changes);
    },
    [searchParams]
  );

  return {
    searchParams,
    setSearchParam,
    setSearchParams,
    getNowSearchParams,
    getNowQueryParams,
    removeSearchParam,
  };
};
